/* eslint-disable react/require-default-props */
import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { CircularProgress, useMediaQuery } from '@mui/material';
import ModalHeader from './ModalHeader';
import SideBarSection from './SideBarSection';
import ModalFooter from './ModalFooter';
import { FooterAction, Section } from '../../interfaces';
import './modal.css';

interface Props {
  backButton?: boolean;
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>> | (() => void);
  name: string;
  sections: Section[];
  footerActions?: FooterAction[];
  moreActions?: FooterAction[];
  isLoading?: boolean;
  topContent?: React.ReactNode;
  redesignActive?: boolean;
  backdropColor?: string;
  /** One-line title with an ellipsis, for sidebars that repeat the full name below */
  truncateTitle?: boolean;
}

const SideBarModal = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      backButton,
      modalOpen,
      setModalOpen,
      name,
      sections,
      footerActions,
      moreActions,
      isLoading,
      topContent,
      redesignActive,
      backdropColor,
      truncateTitle,
    },
    ref,
  ) => {
    const matches = useMediaQuery('(min-width:900px)');

    const style = {
      position: 'absolute' as 'absolute',
      top: '0px',
      right: '0px',
      width: matches ? 400 : '100%',
      bgcolor: redesignActive ? 'var(--sidebar-surface)' : '#f9f9f9',
      boxShadow: '-5px 0px 10px rgb(0 0 0 / 10%)',
      overflowY: 'scroll',
      height: '100dvh',
    };

    const handleClose = React.useCallback(() => {
      setModalOpen(false);
    }, [setModalOpen]);

    return (
      <div>
        <Modal
          open={modalOpen}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          className={
            truncateTitle ? 'sidebar-modal truncate-title' : 'sidebar-modal'
          }
          slotProps={
            backdropColor
              ? { backdrop: { sx: { backgroundColor: backdropColor } } }
              : undefined
          }>
          <Box sx={style}>
            <div ref={ref} className="printable-container">
              {(!redesignActive || isLoading) && (
                <ModalHeader
                  name={name}
                  handleClose={handleClose}
                  moreActions={moreActions}
                  backButton={backButton}
                />
              )}
              {topContent && !isLoading && redesignActive && (
                <Box sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  {topContent}
                </Box>
              )}
              {topContent && !isLoading && !redesignActive && topContent}
              {!isLoading
                ? sections.map(section => (
                    <SideBarSection
                      name={section.name}
                      title={section.title}
                      rows={section.rows}
                      modalName={name}
                      values={section.values}
                      key={section.name}
                      buttonText={section.buttonText}
                      buttonAction={section.buttonAction}
                      noGap={section.noGap}
                      defaultExpanded={section.defaultExpanded}
                      hideHeader={section.hideHeader}
                      summary={section.summary}
                      microLabel={section.microLabel}
                      flat={redesignActive}
                    />
                  ))
                : null}

              {isLoading && (
                <Box
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </div>
            <div className="push" />
            {footerActions?.length && !isLoading ? (
              <ModalFooter footerActions={footerActions} />
            ) : null}
          </Box>
        </Modal>
      </div>
    );
  },
);

export default SideBarModal;
